import "components/supplier_admin/assembly_queue/show";

import applyPayload from "components/supplier_admin/assembly_queue/assembly_queue_dom";
import initQueueCardActions from "components/supplier_admin/assembly_queue/queue_card/queue_card";
import {
  requestAssemblyQueue,
  requestSignal,
  undoSignal
} from "components/supplier_admin/assembly_queue/assembly_queue_api";
import {
  startPolling,
  pausePollingForInFlight,
  resumePollingForInFlight,
  handleVisibilityChange
} from "components/supplier_admin/shared/queue_polling";
import {
  setButtonLoading as setSharedButtonLoading,
  clearButtonLoading as clearSharedButtonLoading
} from "components/supplier_admin/shared/action_button/action_button_loading";

const DEFAULT_PAGE = "1";
const DEFAULT_PER_PAGE = "10";
const DEFAULT_STATUS = "active";

const SELECTORS = {
  container: ".js-data-assembly-queue",
  list: ".js-assembly-queue-list",
  summaryTotal: ".js-assembly-queue-summary-total",
  pagination: ".js-assembly-queue-pagination",
  emptyState: ".js-assembly-queue-empty-state",
  card: ".js-assembly-queue-card",
  actionButton: ".js-assembly-queue-action"
};
const CLASSES = {
  loading: "printer-queue-card__action--loading"
};

const getCardButtons = $button => {
  if (!$button) return [];
  const $card = $button.closest(SELECTORS.card);
  return $card ? Array.from($card.querySelectorAll(SELECTORS.actionButton)) : [];
};

const setButtonsDisabled = ($buttons, disabled) => {
  for (const $actionButton of $buttons) {
    $actionButton.disabled = disabled;
    if (disabled) {
      $actionButton.setAttribute("aria-disabled", "true");
    } else {
      $actionButton.removeAttribute("aria-disabled");
    }
  }
};

const setButtonLoading = ({ $button, $cardButtons }) => {
  if (!$button) return;
  setButtonsDisabled($cardButtons, true);
  setSharedButtonLoading($button, CLASSES.loading);
};

const clearButtonLoading = ({ $button, $cardButtons }) => {
  if (!$button) return;
  clearSharedButtonLoading($button, CLASSES.loading);
  setButtonsDisabled($cardButtons, false);
};

const executeQueueAction = ({ action, shipmentId, signalType, signalId }) =>
  action === "undo"
    ? undoSignal({ shipmentId, signalId })
    : requestSignal({ shipmentId, signalType });

const handleQueueAction = async ({ detail, fetchQueueFn, inFlightActionsRef, pollTimerIdRef }) => {
  if (!detail) return;
  const { action, shipmentId, signalType, signalId, button: $button } = detail;
  if (!shipmentId) return;
  if ($button && $button.disabled) return;

  const $cardButtons = getCardButtons($button);
  setButtonLoading({ $button, $cardButtons });
  pausePollingForInFlight({ inFlightActionsRef, pollTimerIdRef });
  const request = executeQueueAction({ action, shipmentId, signalType, signalId });

  try {
    await request;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Assembly Queue] Signal status update failed.", error);
  } finally {
    await fetchQueueFn();
    resumePollingForInFlight({ inFlightActionsRef, pollTimerIdRef, fetchQueueFn });
    clearButtonLoading({ $button, $cardButtons });
  }
};

const fetchQueue = async ({
  state,
  $listEl,
  $container,
  $summaryTotalEl,
  $paginationEl,
  $emptyStateEl,
  isFetchingRef
}) => {
  if (isFetchingRef.value) return;
  isFetchingRef.value = true;

  try {
    const payload = await requestAssemblyQueue(state);
    if (!payload) return;
    applyPayload({
      payload,
      state,
      $container,
      $listEl,
      $summaryTotalEl,
      $emptyStateEl,
      $paginationEl
    });
  } catch (err) {
    console.error("Assembly queue fetch failed:", err);
  } finally {
    isFetchingRef.value = false;
  }
};

const queryDomElements = $container => ({
  $listEl: $container.querySelector(SELECTORS.list),
  $summaryTotalEl: $container.querySelector(SELECTORS.summaryTotal),
  $paginationEl: $container.querySelector(SELECTORS.pagination),
  $emptyStateEl: $container.querySelector(SELECTORS.emptyState)
});

const buildInitialState = $container => ({
  date: $container.dataset.assemblyQueueDate || "",
  page: $container.dataset.assemblyQueuePage || DEFAULT_PAGE,
  perPage: $container.dataset.assemblyQueuePerPage || DEFAULT_PER_PAGE,
  lastUpdated: $container.dataset.assemblyQueueLastUpdated || "",
  status: $container.dataset.assemblyQueueStatus || DEFAULT_STATUS
});

const initAssemblyQueue = $container => {
  const { $listEl, $summaryTotalEl, $paginationEl, $emptyStateEl } = queryDomElements($container);
  if (!$listEl) return;

  const state = buildInitialState($container);
  const pollTimerIdRef = { value: null };
  const pauseTimerIdRef = { value: null };
  const inFlightActionsRef = { value: 0 };
  const isFetchingRef = { value: false };

  const fetchQueueFn = () =>
    fetchQueue({
      state,
      $listEl,
      $container,
      $summaryTotalEl,
      $paginationEl,
      $emptyStateEl,
      isFetchingRef
    });

  initQueueCardActions($container);
  $container.addEventListener("assembly-queue:signal-change-action", event =>
    handleQueueAction({
      detail: event.detail,
      fetchQueueFn,
      inFlightActionsRef,
      pollTimerIdRef
    })
  );

  document.addEventListener("visibilitychange", () =>
    handleVisibilityChange({ pauseTimerIdRef, pollTimerIdRef, fetchQueueFn, inFlightActionsRef })
  );
  startPolling({ pollTimerIdRef, fetchQueueFn });
};

document.addEventListener("DOMContentLoaded", () => {
  const $container = document.querySelector(SELECTORS.container);
  if ($container) initAssemblyQueue($container);
});
